import type { MouseEvent, RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import type { VideoUploadProps } from '../../VideoUpload.types';
import { getVideoDuration, getVideoAspectRatio } from '../../VideoUpload.utils';
import { useFullscreen } from './useFullscreen';
import { usePlayback } from './usePlayback';
import { usePlaybackSettings } from './usePlaybackSettings';
import { usePictureInPicture } from './usePictureInPicture';
import { useVolume } from './useVolume';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { useElementsVisibility } from './useElementsVisibility';
import { useBuffer } from './useBuffer';
import { useTimingTrap } from './useTimingTrap';

type UseVideoPlayerConfig = {
  videoRef: RefObject<HTMLVideoElement>;
  containerRef: RefObject<HTMLDivElement>;
  video?: VideoUploadProps['video'];
  autoplay: boolean;
  playbackRate: number;
  enableTitle: boolean;
  enableControls: boolean;
  loop: boolean;
  startTime?: number;
  endTime?: number;
};

export const useVideoPlayer = ({
  videoRef,
  containerRef,
  video,
  autoplay,
  playbackRate,
  enableTitle,
  enableControls,
  loop,
  startTime,
  endTime,
}: UseVideoPlayerConfig) => {
  const aspectRatio = getVideoAspectRatio(video);
  const duration = getVideoDuration(videoRef.current);
  const timings = {
    startTime: startTime ?? 0,
    endTime: endTime ?? duration,
  };
  const buffer = useBuffer({
    videoRef,
    startTime: timings.startTime,
    endTime: timings.endTime,
  });
  const playback = usePlayback(videoRef, autoplay);
  const playbackSettings = usePlaybackSettings(videoRef, playbackRate);
  const audio = useVolume(videoRef, video);
  const pip = usePictureInPicture(videoRef);
  const fullscreen = useFullscreen(containerRef, videoRef);

  const { handleKeyDown } = useKeyboardShortcuts({
    videoRef,
    startTime: timings.startTime,
    endTime: timings.endTime,
    onPlayPause: playback.togglePlay,
    onSeekForward: playback.seekForward,
    onSeekBackward: playback.seekBackward,
    onToggleMuted: audio.toggleMuted,
    onIncreaseVolume: audio.increaseVolume,
    onDecreaseVolume: audio.decreaseVolume,
    onToggleFullscreen: fullscreen.toggleFullscreen,
  });

  const {
    showTitle,
    showControls,
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    handleFocus,
  } = useElementsVisibility({
    enableControls,
    enableTitle,
    isPlaying: playback.isPlaying,
  });

  const showPlayButton = useMemo(() => {
    const controlsHidden = !showControls;
    const isPaused = !playback.isPlaying;
    const isAtStart = playback.currentTime <= timings.startTime;

    return isPaused && (isAtStart || controlsHidden);
  }, [
    playback.currentTime,
    playback.isPlaying,
    showControls,
    timings.startTime,
  ]);

  const showSpinner = playback.isWaiting && playback.isPlaying;

  const { toggleFullscreen } = fullscreen;
  const { play, togglePlay } = playback;

  const handleVideoContainerClick = useCallback(
    async (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      await togglePlay();
    },
    [togglePlay],
  );

  const handlePlayButtonOverlayClick = useCallback(
    async (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      await play();
      containerRef.current?.focus();
    },
    [containerRef, play],
  );

  const handleVideoContainerDoubleClick = useCallback(
    async (event: MouseEvent) => {
      event.preventDefault();
      await toggleFullscreen();
    },
    [toggleFullscreen],
  );

  const handlers = {
    onVideoPlayerKeyDown: handleKeyDown,
    onVideoPlayerMouseEnter: handleMouseEnter,
    onVideoPlayerMouseMove: handleMouseMove,
    onVideoPlayerMouseLeave: handleMouseLeave,
    onVideoPlayerFocus: handleFocus,
    onVideoContainerClick: handleVideoContainerClick,
    onVideoContainerDoubleClick: handleVideoContainerDoubleClick,
    onPlayButtonOverlayClick: handlePlayButtonOverlayClick,
  };

  useTimingTrap({
    videoRef,
    duration,
    startTime: timings.startTime,
    endTime: timings.endTime,
    loop,
    onPause: playback.pause,
  });

  return {
    timings,
    playback,
    playbackSettings,
    audio,
    pip,
    fullscreen,
    buffer,
    aspectRatio,
    duration,
    showPlayButton,
    showTitle,
    showControls,
    showSpinner,
    handlers,
  };
};
